import React, { useEffect, useRef, useState } from 'react';
import { MarkdownLite } from '../pages/Plan';
import { acceptLegal, legalStatus } from '../data/legal';
import './Clickwrap.css';

function reachedEnd(el) {
  if (!el) return false;
  return el.scrollHeight - el.scrollTop - el.clientHeight <= 12;
}

// All documents in one scroller, stacked in order. Agree stays off until
// the reader reaches the bottom of the stack (a stack that fits without
// scrolling counts as read). Agree calls onAgree with the documents seen.
export default function Clickwrap({ docs, onAgree, busy = false, error = '', intro }) {
  const [atEnd, setAtEnd] = useState(false);
  const scroller = useRef(null);
  const key = (docs || []).map((d) => d.id).join('|');

  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    const check = () => setAtEnd(reachedEnd(el));
    check();
    const id = requestAnimationFrame(check);
    el.addEventListener('scroll', check);
    window.addEventListener('resize', check);
    return () => {
      cancelAnimationFrame(id);
      el.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [key]);

  if (!docs?.length) return null;

  const agree = async () => {
    if (!atEnd || busy) return;
    await onAgree(docs);
  };

  return (
    <div className="cw">
      {intro && <p className="cs-help cw-intro">{intro}</p>}
      <p className="cw-progress">{docs.length} documents · {docs.map((d) => d.title).join(' · ')}</p>
      <div className="cw-card">
        <div className="cw-body" ref={scroller} tabIndex={0} role="region" aria-label="Agreements">
          {docs.map((doc) => (
            <section key={doc.id} className="cw-doc">
              <div className="cw-head">
                <strong>{doc.title}</strong>
                <span className="cs-help" style={{ margin: 0 }}>version {doc.version}</span>
              </div>
              <MarkdownLite text={doc.body} />
            </section>
          ))}
        </div>
      </div>
      {error && <p className="cs-help cw-err">{error}</p>}
      <div className="cw-foot">
        <button type="button" className="cs-btn cs-btn-primary" disabled={!atEnd || busy} onClick={agree}>
          {busy ? 'Saving…' : atEnd ? 'Agree' : 'Scroll to the end to agree'}
        </button>
      </div>
    </div>
  );
}

// Shown before a self-serve charge. onDone runs after the organization
// acceptance is recorded, so the caller can open Stripe.
export function OrgAgree({ orgId, docs, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!docs?.length) return null;
  return (
    <Clickwrap
      docs={docs}
      busy={busy}
      error={error}
      intro="Agree on behalf of the organization: the terms, privacy policy, and end user agreement."
      onAgree={async (seen) => {
        setBusy(true);
        setError('');
        try {
          await acceptLegal({ scope: 'org', orgId, docIds: seen.map((d) => d.id) });
          await onDone();
        } catch (err) {
          setError(err.message);
          setBusy(false);
        }
      }}
    />
  );
}

const PENDING_USER_KEY = 'nv.clickwrapUser';

// Signed-in console gate. The account is not usable until the person has
// accepted the current versions. SSO and any signup that skipped the form
// land here. Signup on /login unmounts as soon as the account exists, so
// a pending acceptance stored before sign-up is finished here.
export function ClickwrapGate({ children }) {
  const [state, setState] = useState({ loading: true, userOk: true, docs: [], error: '' });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: '' }));
    const finishPending = async () => {
      let raw = null;
      try { raw = sessionStorage.getItem(PENDING_USER_KEY); } catch { /* ignore */ }
      if (!raw) return;
      try {
        const docIds = JSON.parse(raw);
        if (Array.isArray(docIds) && docIds.length) {
          await acceptLegal({ scope: 'user', docIds });
        }
        sessionStorage.removeItem(PENDING_USER_KEY);
      } catch (err) {
        if (err.status === 409) {
          try { sessionStorage.removeItem(PENDING_USER_KEY); } catch { /* ignore */ }
        }
      }
    };
    finishPending()
      .then(() => legalStatus())
      .then((data) => setState({
        loading: false,
        userOk: !!data.userOk,
        docs: data.agreements || [],
        error: '',
      }))
      .catch((err) => setState({ loading: false, userOk: false, docs: [], error: err.message }));
  };

  useEffect(() => { load(); }, []);

  if (state.loading) return <div className="cs-auth-wrap">Loading…</div>;
  if (state.userOk) return children;
  if (!state.docs.length) {
    return (
      <div className="cs-auth-wrap">
        <div className="cs-empty" style={{ maxWidth: 480 }}>
          <p>The agreements could not be loaded.</p>
          {state.error && <p className="cs-help">{state.error}</p>}
          <button type="button" className="cs-btn cs-btn-primary" onClick={load}>Try again</button>
        </div>
      </div>
    );
  }
  return (
    <div className="cs-auth-wrap">
      <div style={{ width: 'min(960px, 100%)', padding: 20 }}>
        <h2 style={{ fontSize: 22, marginTop: 0 }}>Agreements</h2>
        <Clickwrap
          docs={state.docs}
          busy={busy}
          error={state.error}
          intro="Read to the end, then agree: the terms, privacy policy, and end user agreement."
          onAgree={async (docs) => {
            setBusy(true);
            try {
              await acceptLegal({ scope: 'user', docIds: docs.map((d) => d.id) });
              load();
            } catch (err) {
              setState((s) => ({ ...s, error: err.message }));
            } finally {
              setBusy(false);
            }
          }}
        />
      </div>
    </div>
  );
}
